import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Checkbox,
  Text,
  Box,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { usePlaylistsInfinite } from '../../media-library/hooks/usePlaylists';
import type { BlockPlaylist } from './SortablePlaylistList';

interface AddPlaylistModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingPlaylistIds: Set<string>;
  onAdd: (playlists: BlockPlaylist[]) => void;
}

export function AddPlaylistModal({ isOpen, onClose, existingPlaylistIds, onAdd }: AddPlaylistModalProps) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Map<string, BlockPlaylist>>(new Map());

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Map());
      setSearchInput('');
      setSearch('');
    }
  }, [isOpen]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = usePlaylistsInfinite({
    search: search || undefined,
  });
  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  const toggle = (id: string, name: string, trackCount: number) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, { playlistId: id, playlistName: name, trackCount });
      return next;
    });

  const handleAdd = () => {
    onAdd([...selected.values()]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>{t('scenarios.addPlaylists')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder={t('playlists.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>

          {isLoading ? (
            <Flex justify="center" py={10}>
              <Spinner color="brand.500" />
            </Flex>
          ) : items.length === 0 ? (
            <Text color="gray.400" py={8} textAlign="center">
              {t('playlists.empty')}
            </Text>
          ) : (
            <VStack align="stretch" spacing={0}>
              {items.map((p) => {
                const already = existingPlaylistIds.has(p.id);
                return (
                  <HStack key={p.id} py={2} px={1} spacing={3} _hover={{ bg: 'gray.50' }} borderRadius="md" opacity={already ? 0.5 : 1}>
                    <Checkbox
                      isChecked={selected.has(p.id) || already}
                      isDisabled={already}
                      onChange={() => toggle(p.id, p.name, p.trackCount)}
                      colorScheme="brand"
                    />
                    <Box flex={1} minW={0}>
                      <Text fontWeight="500" noOfLines={1}>
                        {p.name}
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        {t('playlists.trackCountLabel', { count: p.trackCount })}
                      </Text>
                    </Box>
                    {already && (
                      <Text fontSize="xs" color="gray.400">
                        {t('playlists.alreadyAdded')}
                      </Text>
                    )}
                  </HStack>
                );
              })}
              {hasNextPage && (
                <Flex justify="center" mt={3}>
                  <Button size="sm" variant="ghost" onClick={() => fetchNextPage()} isLoading={isFetchingNextPage}>
                    {t('playlists.loadMore')}
                  </Button>
                </Flex>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button colorScheme="brand" onClick={handleAdd} isDisabled={selected.size === 0}>
            {t('playlists.addSelected', { count: selected.size })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AddPlaylistModal;
